import { useTranslation } from "next-i18next";

import { ProductBadge } from "@/components/product/ProductBadge";
import { PRO_BUYER_RESERVATIONS_ENABLED } from "@/config";
import { ProBuyerPriceLabel } from "@/features/pro-buyer-price/components/ProBuyerPriceMarks";
import { useMyReservations } from "@/features/pro-buyer-reservations/hooks/useReservations";
import { isOnHoldUntilActive } from "@/features/pro-buyer-reservations/utils/reservation-format";
import { useProductCard } from "@/features/search/components/product-card/product-card.context";

/**
 * Overlay badges. Positioned absolutely, so render inside the image's relative
 * container.
 */
export const ProductCardBadges = () => {
  const { t } = useTranslation();
  const { product, proBuyerPrice } = useProductCard();
  const { sold, discountPercentage } = product;
  // Fail-soft: [] with the flag off, signed out or a gateway without the field.
  const myReservations = useMyReservations();

  /* WHO-5947 F8: the holder reads "Reserved for you"; an open request shows nothing. */
  const reservedForMe =
    !sold &&
    myReservations.some(
      reservation =>
        reservation.productId === product.id &&
        reservation.status === "ACCEPTED"
    );

  if (reservedForMe) {
    return (
      <div className="pointer-events-none absolute left-2 top-2 flex flex-col items-start space-y-1">
        <ProductBadge tone="reservedMine">
          {t("common:reservation-reserved-for-you-label", "Reserved for you")}
        </ProductBadge>
      </div>
    );
  }

  /* WHO-5947: search hit `onHoldUntil` / `on_hold_until` (unix seconds) in the future. */
  if (
    PRO_BUYER_RESERVATIONS_ENABLED &&
    !sold &&
    isOnHoldUntilActive(product.onHoldUntil ?? product.on_hold_until)
  ) {
    return (
      <div className="pointer-events-none absolute left-2 top-2 flex flex-col items-start space-y-1">
        <ProductBadge tone="reserved">
          {t("common:reservation-reserved-label", "Reserved")}
        </ProductBadge>
      </div>
    );
  }

  /* WHO-5743: a Pro Buyer sees only the Pro Buyer label, never a sale percentage. */
  if (proBuyerPrice && !sold) {
    return (
      <div className="pointer-events-none absolute left-2 top-2 flex flex-col items-start space-y-1">
        <ProBuyerPriceLabel />
      </div>
    );
  }

  if (!sold && discountPercentage == null) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute left-2 top-2 flex flex-col items-start space-y-1">
      {/* Sold wins over the discount — the price is no longer on offer. */}
      {sold ? (
        <ProductBadge tone="sold">
          {t("common:auction-state-already-sold")}
        </ProductBadge>
      ) : (
        <ProductBadge tone="discount">-{discountPercentage}%</ProductBadge>
      )}
    </div>
  );
};
